import { FiHelpCircle } from 'react-icons/fi'
import { MdPriorityHigh } from 'react-icons/md'

import { Form } from '@unform/web'
import { t } from 'i18next'

import { AlertWithTitle } from '~/components/AlertWithTitle'
import { Button } from '~/components/Buttons'
import { IconCircle } from '~/components/Icon/Circle'
import Tooltip from '~/components/Tooltip'
import { FormGroup, Label, Textarea } from '~/components/unform'

import { DestinationUrlValue } from '../../../Campaign/DestinationUrls/elements/Fields'
import { formatReviewDestinationUrls } from '../../../Campaign/DestinationUrls/helpers/formatReviewDestinationUrls'

import * as S from './styles'

interface Props {
  contentModal: ContentModal
  Modal: React.FC
  showConfirmationModal: boolean
  toggleConfirmationModal(): void
  handleSubmit: ({
    dataForm,
    status
  }: {
    dataForm: FormData
    status: CampaignStatus
    action: CampaignActions
  }) => void
  alertVariant?: AlertProps['variant']
  destinationUrls?: DestinationUrlValue[]
  emptyDestinationUrl?: boolean
}

export const ModalConfirmations = ({
  Modal,
  showConfirmationModal,
  toggleConfirmationModal,
  contentModal,
  handleSubmit,
  alertVariant,
  destinationUrls,
  emptyDestinationUrl = false
}: Props) => {
  const filledDestinationUrls = emptyDestinationUrl
    ? []
    : formatReviewDestinationUrls(destinationUrls)
  return (
    <Modal
      title={contentModal?.titleModal}
      isOpen={showConfirmationModal}
      onClose={toggleConfirmationModal}
      maxWidth={contentModal?.summary ? '700px' : undefined}
    >
      {!!contentModal?.summary && (
        <S.Summary>
          <S.SummaryRow>
            <S.SummaryLabel>
              {t('rm:reviewCampaign.approve.summary.period')}
            </S.SummaryLabel>
            <S.SummaryValue>{contentModal.summary.period}</S.SummaryValue>
          </S.SummaryRow>

          <S.SummaryRow>
            <S.SummaryLabel>{contentModal.summary.mediaLabel}</S.SummaryLabel>
            <S.SummaryValue>{contentModal.summary.mediaCount}</S.SummaryValue>
          </S.SummaryRow>

          {(!!contentModal?.editableDestinationUrls ||
            !!destinationUrls?.length ||
            emptyDestinationUrl) && (
            <S.SummaryRow>
              <S.SummaryLabel>
                {t('rm:reviewCampaign.approve.label.destinationUrl')}
              </S.SummaryLabel>
              <S.SummaryValue>
                {filledDestinationUrls?.length ? (
                  filledDestinationUrls.map(({ url, brand }) => (
                    <p key={`${brand}-${url}`}>{url}</p>
                  ))
                ) : (
                  <S.NoDestinationUrl>
                    <S.NoDestinationUrlCopy>
                      {t('rm:noDestinationUrl')}
                      <Tooltip
                        text={t('rm:noDestinationUrl.tooltip')}
                        icon={<FiHelpCircle />}
                      />
                    </S.NoDestinationUrlCopy>
                    <IconCircle icon={MdPriorityHigh} template="warningFill" />
                  </S.NoDestinationUrl>
                )}
              </S.SummaryValue>
            </S.SummaryRow>
          )}

          {contentModal.summary.dailyBudget !== undefined && (
            <S.SummaryRow>
              <S.SummaryLabel>{t('rm:dailyBudget')}</S.SummaryLabel>
              <S.SummaryValue>
                {contentModal.summary.dailyBudget}
              </S.SummaryValue>
            </S.SummaryRow>
          )}
        </S.Summary>
      )}

      <S.Container>
        <Form
          onSubmit={dataForm =>
            handleSubmit({
              ...dataForm,
              status: contentModal?.status,
              action: contentModal?.action
            })
          }
        >
          <FormGroup>
            {!!contentModal?.labelMessage && (
              <Label htmlFor="note" isRequired={contentModal?.noteIsRequired}>
                {contentModal?.labelMessage}
              </Label>
            )}
            {!!contentModal?.subtitletLabelMessage && (
              <small> {contentModal?.subtitletLabelMessage}</small>
            )}
            {!!contentModal?.labelMessage && (
              <Textarea
                name="note"
                minHeight="4px"
                required={contentModal?.noteIsRequired}
              />
            )}
          </FormGroup>

          {!!contentModal?.alertMessage && (
            <S.WrapperAlert>
              <AlertWithTitle
                title={contentModal?.titleAlertMessage}
                description={contentModal?.alertMessage}
                variant={alertVariant}
              />
            </S.WrapperAlert>
          )}

          <S.WrapperButtons hasCancelButton={contentModal?.hasCancelButton}>
            {contentModal?.hasCancelButton && (
              <Button template="transparent" onClick={toggleConfirmationModal}>
                Cancelar
              </Button>
            )}
            <Button
              template={contentModal?.templateButton || 'primary'}
              customWidth={!contentModal?.hasCancelButton ? '100%' : '0'}
              type="submit"
            >
              {contentModal?.textButton || t('rm:Continue')}
            </Button>
          </S.WrapperButtons>
        </Form>
      </S.Container>
    </Modal>
  )
}
